import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { StyledEnabledToggle } from "pages/CreateOrder/CreateOrder.styled";
import { getPaymentPlanEnabled } from "pages/CreateOrder/utils/helpers";
import { Card } from "primereact/card";
import { SelectButton } from "primereact/selectbutton";
import { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { IAccountResponse } from "types/Account/AccountResponse";
import { IPaymentPlan } from "types/Payment";

import { StepCard } from "../StepCard";
import { StepKey } from "../StepCard/helper";
import { useStepStatus } from "../StepCard/useStepStatus";
import { DownpaymentOnlySection } from "./components/DownpaymentOnlySection";
import { PaymentPlanSection } from "./components/PaymentPlanSection";
import { PAYMENT_OPTION_META, buildRevalidateIfFilled } from "./helper";

interface IPaymentPlanProps {
  accountData: IAccountResponse;
  paymentPlan?: IPaymentPlan;
  isRedo?: boolean;
  isDraft?: boolean;
  isCopy?: boolean;
}

const StyledSelectButton = styled(SelectButton)`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  /* Every rule is prefixed with &.p-selectbutton so it outranks the theme's
     own .p-selectbutton .p-button... rules in clear-theme-overrides.css. */
  &.p-selectbutton .p-button {
    flex: 1 1 260px;
    margin: 0;
    padding: 1rem;
    border-radius: 12px !important;

    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 0.75rem;
    text-align: left;
    white-space: normal;

    background: var(--brand-gray-50);
    border: 1px solid var(--brand-gray-200);
    color: var(--brand-black);
    box-shadow: none;
  }

  /* Hover keeps the card fill and only tints the border. */
  &.p-selectbutton .p-button:not(.p-disabled):not(.p-highlight):hover {
    background: var(--brand-gray-50);
    border-color: color-mix(
      in srgb,
      var(--brand-purple-500) 45%,
      var(--brand-white)
    );
    color: var(--brand-black);
  }

  /* The selected card keeps the same fill; only the border and icon tile
     change. The inset ring thickens the border without shifting layout. */
  &.p-selectbutton .p-button.p-highlight,
  &.p-selectbutton .p-button.p-highlight:hover {
    background: var(--brand-gray-50);
    border-color: var(--brand-purple-700);
    color: var(--brand-black);
    box-shadow: inset 0 0 0 1px var(--brand-purple-700);
  }

  &.p-selectbutton .p-button:focus {
    box-shadow: 0 0 0 3px
      color-mix(in srgb, var(--brand-purple-500) 20%, transparent);
  }

  .option-icon {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 10px;

    display: flex;
    align-items: center;
    justify-content: center;

    background: var(--brand-gray-100);
    color: var(--brand-gray-600);
  }

  &.p-selectbutton .p-button.p-highlight .option-icon {
    background: var(--brand-purple-700);
    color: var(--brand-white);
  }

  .option-title {
    font-size: 1rem;
    font-weight: var(--font-weight-semibold);
    color: var(--brand-black);
  }

  .option-description {
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.4;
    color: var(--brand-gray-500);
  }
`;

function renderPaymentOption(option: { label: string; value: string }) {
  const meta = PAYMENT_OPTION_META[option.value];

  return (
    <>
      <span className="option-icon">
        <i className={meta?.icon || "pi pi-credit-card"} />
      </span>

      <span className="flex flex-column gap-1">
        <span className="option-title">{option.label}</span>
        {!!meta?.description && (
          <span className="option-description">{meta.description}</span>
        )}
      </span>
    </>
  );
}

export function PaymentPlan({
  accountData,
  paymentPlan,
  isRedo,
  isDraft,
  isCopy,
}: IPaymentPlanProps) {
  const { watch, setValue, trigger, control, getValues, clearErrors } =
    useFormContext();

  const revalidateIfFilled = buildRevalidateIfFilled({
    getValues,
    trigger,
    clearErrors,
  });
  const status = useStepStatus(StepKey.PAYMENT);
  const isDownPaymentOnly = watch("isDownPaymentOnly");
  const isSetAmount = watch("isSetAmount");
  const paymentPlanEnabledField = watch("paymentPlanEnabled");
  const orderTypeField = watch("orderType");

  const selectedOption = useMemo(() => {
    if (isDownPaymentOnly) {
      return "downPaymentOnly";
    }
    if (isSetAmount) {
      return "setAmount";
    }
    return "paymentPlan";
  }, [isDownPaymentOnly, isSetAmount]);

  const paymentPlanIsEnabledForOrderType = useMemo(
    () =>
      getPaymentPlanEnabled(
        orderTypeField,
        accountData.data.paymentPlanTypeEnabled
      ),
    [orderTypeField, accountData.data.paymentPlanTypeEnabled]
  );

  const { partialPaymentsEnabled, providerDistributionLogic } =
    accountData.data;

  const availableOptions = useMemo(() => {
    const options = [];
    if (paymentPlan) {
      if (paymentPlan.isDownPaymentOnly) {
        if (paymentPlanIsEnabledForOrderType) {
          options.push({
            label: "Set number of payments",
            value: "paymentPlan",
          });
          options.push({ label: "Set amount", value: "setAmount" });
        }
      } else if (!isDraft || paymentPlanIsEnabledForOrderType) {
        options.push({ label: "Set number of payments", value: "paymentPlan" });
        options.push({ label: "Set amount", value: "setAmount" });
      }

      if (partialPaymentsEnabled) {
        options.push({ label: "Downpayment Only", value: "downPaymentOnly" });
      }
      return options;
    }
    if (paymentPlanIsEnabledForOrderType) {
      options.push({ label: "Set number of payments", value: "paymentPlan" });
      options.push({ label: "Set amount", value: "setAmount" });
    }
    if (partialPaymentsEnabled) {
      options.push({ label: "Downpayment Only", value: "downPaymentOnly" });
    }
    return options;
  }, [paymentPlanIsEnabledForOrderType, partialPaymentsEnabled, isDraft]);

  const handlePaymentOptionChange = (e: any) => {
    if (e.value === "downPaymentOnly") {
      setValue("isDownPaymentOnly", true);
      setValue("isSetAmount", false);
      setValue("providerDistributionLogic", providerDistributionLogic);
      revalidateIfFilled("downpaymentOnlyPercent");
      revalidateIfFilled("downpaymentOnlyFlatAmount");
    } else {
      setValue("isDownPaymentOnly", false);
      setValue("isSetAmount", e.value === "setAmount");

      if (e.value === "setAmount") {
        setValue("installmentAmount", null);
        setValue("paymentPlanMinDownPaymentType", "Flat amount");
        setValue("paymentPlanMinDownPaymentPercent", null);
        setValue("paymentPlanMinDownPaymentFlat", null);
        revalidateIfFilled("installmentAmount");
        revalidateIfFilled("paymentPlanMinDownPaymentPercent");
        revalidateIfFilled("paymentPlanMinDownPaymentFlat");
      }
      setValue("downpaymentOnlyType", "Percentage");
      setValue("downpaymentOnlyPercent", null);
      setValue("downpaymentOnlyFlatAmount", null);
      // clear validation errors
      revalidateIfFilled("downpaymentOnlyPercent");
      revalidateIfFilled("downpaymentOnlyFlatAmount");
    }
  };

  // Auto-set isPartialPayment based on what's available

  useEffect(() => {
    if (availableOptions.length === 0) {
      setValue("isDownPaymentOnly", false);
      setValue("isSetAmount", false);
      setValue("paymentPlanEnabled", false);
    } else if (availableOptions.length === 1) {
      setValue(
        "isDownPaymentOnly",
        availableOptions[0].value === "downPaymentOnly"
      );
      setValue("isSetAmount", false);
    } else {
      setValue("isDownPaymentOnly", paymentPlan?.isDownPaymentOnly);
      setValue(
        "isSetAmount",
        paymentPlan?.calculationBasis === "InstallmentAmount" &&
          (isRedo || isCopy || isDraft)
      );
    }
  }, [availableOptions, isRedo, isCopy, isDraft]);

  const planIsOff = !paymentPlanEnabledField;

  if (!availableOptions.length) {
    return null;
  }

  return (
    <StepCard
      status={status}
      icon={<i className="pi pi-credit-card text-base" />}
      title="Payment Options"
      description="Let the patient split their responsibility into a payment plan"
      data-testid="paymentPlan_Card"
    >
      <div
        className="p-4"
        style={{
          borderRadius: "16px",
          background: planIsOff
            ? "var(--brand-gray-50)"
            : "color-mix(in srgb, var(--brand-purple-500) 4%, var(--brand-white))",
          border: planIsOff
            ? "1px solid var(--brand-gray-200)"
            : "1px solid color-mix(in srgb, var(--brand-purple-500) 30%, var(--brand-white))",
        }}
      >
        <div className="flex align-items-start justify-content-between gap-3">
          <div>
            <div
              className="text-base font-semibold"
              style={{ color: "var(--brand-black)" }}
            >
              Offer a payment plan
            </div>
            <div
              className="text-sm mt-1"
              style={{ color: "var(--brand-gray-500)" }}
            >
              {planIsOff
                ? "The patient will pay their full responsibility at checkout"
                : "Configure installments for the patient balance"}
            </div>
          </div>

          <Controller
            name="paymentPlanEnabled"
            control={control}
            render={({ field: { onChange, value } }) => (
              <StyledEnabledToggle
                type="button"
                id="paymentPlanEnabled"
                data-testid="paymentPlanEnabled"
                $enabled={!!value}
                aria-pressed={!!value}
                onClick={() => onChange(!value)}
              >
                <span className="dot" />
                {value ? "Enabled" : "Disabled"}
              </StyledEnabledToggle>
            )}
          />
        </div>

        <div>
          <div
            className="my-4"
            style={{
              borderTop:
                "1px solid color-mix(in srgb, var(--brand-purple-500) 22%, var(--brand-white))",
            }}
          />

          <div
            className="text-base font-semibold"
            style={{ color: "var(--brand-black)" }}
          >
            How would you like to build this payment plan?
          </div>
          <div
            className="text-sm mt-1 mb-3"
            style={{ color: "var(--brand-gray-500)" }}
          >
            Choose what you want to control and we&apos;ll calculate the rest.
          </div>

          {/* Only show select button if both options are available */}
          {availableOptions.length > 1 && (
            <StyledSelectButton
              id="paymentOption"
              data-testid="paymentOption_SelectButton"
              value={selectedOption}
              options={availableOptions}
              onChange={(e) => e.value && handlePaymentOptionChange(e)}
              optionLabel="label"
              optionValue="value"
              itemTemplate={renderPaymentOption}
            />
          )}
          {!isDownPaymentOnly && (
            <PaymentPlanSection
              accountData={accountData}
              paymentPlan={paymentPlan}
              isRedo={isRedo}
              isDraft={isDraft}
              isCopy={isCopy}
            />
          )}
          {isDownPaymentOnly && (
            <DownpaymentOnlySection
              downpaymentOnlyPaymentPlan={
                paymentPlan?.isDownPaymentOnly ? paymentPlan : undefined
              }
              isRedo={isRedo}
              isDraft={isDraft}
              isCopy={isCopy}
            />
          )}
        </div>
      </div>
    </StepCard>
  );
}
